
import { useCart } from '../../../context/CartContext';
import { useNavigate } from 'react-router-dom';

export default function OrderSummary() {
  const { cartItems, cartTotal, removeFromCart, updateQuantity } = useCart();
  const navigate = useNavigate();
  const shipping = cartTotal >= 100 ? 0 : 7.99;
  const tax = cartTotal * 0.08;
  const grandTotal = cartTotal + shipping + tax;

  return (
    <div className="lg:sticky lg:top-8">
      <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-gray-800">
          <h2 className="text-white text-xl font-bold uppercase tracking-wider">Order Summary</h2>
          <p className="text-gray-500 text-sm mt-1">
            {cartItems.reduce((s, i) => s + i.quantity, 0)} item(s)
          </p>
        </div>

        {/* Free shipping banner */}
        {cartTotal < 100 && (
          <div className="mx-4 mt-4 bg-[#1a1a1a] border border-gray-700 rounded-lg px-4 py-3 flex items-center gap-2">
            <i className="ri-truck-line text-[#8B0000] text-lg"></i>
            <p className="text-gray-400 text-xs">
              Add{' '}
              <span className="text-white font-bold">
                ${(100 - cartTotal).toFixed(2)}
              </span>{' '}
              more for free shipping!
            </p>
          </div>
        )}
        {cartTotal >= 100 && (
          <div className="mx-4 mt-4 bg-[#0d1f0d] border border-green-900 rounded-lg px-4 py-3 flex items-center gap-2">
            <i className="ri-truck-line text-green-500 text-lg"></i>
            <p className="text-green-400 text-xs font-semibold">
              You qualify for FREE shipping!
            </p>
          </div>
        )}

        {/* Items */}
        <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
          {cartItems.map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-3 bg-[#1a1a1a] rounded-lg p-3 border border-gray-800"
            >
              <div className="w-14 h-14 rounded-md overflow-hidden bg-[#0a0a0a] flex-shrink-0">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-semibold truncate">
                  {item.name}
                </p>
                {item.variant && (
                  <p className="text-gray-500 text-xs">{item.variant}</p>
                )}
                {item.size && (
                  <p className="text-gray-500 text-xs">{item.size}</p>
                )}
                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() =>
                        updateQuantity(index, item.quantity - 1)
                      }
                      className="w-5 h-5 flex items-center justify-center bg-[#252525] hover:bg-[#8B0000] text-gray-400 hover:text-white rounded text-xs transition-colors cursor-pointer"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="text-white text-xs w-5 text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() =>
                        updateQuantity(index, item.quantity + 1)
                      }
                      className="w-5 h-5 flex items-center justify-center bg-[#252525] hover:bg-[#8B0000] text-gray-400 hover:text-white rounded text-xs transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <span className="text-gray-300 text-xs font-bold">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => removeFromCart(index)}
                className="w-6 h-6 flex items-center justify-center text-gray-600 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          ))}
        </div>

        {/* Price Breakdown */}
        <div className="p-6 border-t border-gray-800 space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Subtotal</span>
            <span className="text-white">
              ${cartTotal.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Shipping</span>
            <span
              className={
                shipping === 0 ? 'text-green-400 font-semibold' : 'text-white'
              }
            >
              {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Est. Tax (8%)</span>
            <span className="text-white">${tax.toFixed(2)}</span>
          </div>
          <div className="border-t border-gray-800 pt-3 flex justify-between">
            <span className="text-white font-bold text-lg">Total</span>
            <span className="text-[#8B0000] font-bold text-xl">
              ${grandTotal.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="px-6 pb-6 grid grid-cols-3 gap-3">
          {[
            { icon: 'ri-shield-check-line', label: 'Secure Checkout' },
            { icon: 'ri-refund-2-line', label: '30-Day Returns' },
            { icon: 'ri-customer-service-2-line', label: '24/7 Support' },
          ].map((badge) => (
            <div
              key={badge.label}
              className="flex flex-col items-center gap-1 bg-[#1a1a1a] rounded-lg py-3 px-2 border border-gray-800"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${badge.icon} text-[#8B0000] text-lg`}></i>
              </div>
              <span className="text-gray-500 text-[10px] text-center leading-tight">
                {badge.label}
              </span>
            </div>
          ))}
        </div>

        {/* Continue Shopping */}
        <div className="px-6 pb-6">
          <button
            onClick={() => navigate('/')}
            className="w-full border border-gray-700 hover:border-[#8B0000] text-gray-400 hover:text-[#8B0000] font-semibold py-3 rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Continue Shopping
          </button>
        </div>
      </div>
    </div>
  );
}
